/**
 * FlahaSOIL v2 — ReportCoverPage (Phase 10D.1).
 *
 * Full-page, print-ready cover for every professional report.
 * Layout inspired by flahasoil-soil-analysis-cover-reference.png.
 *
 * Brand asset rules:
 *  - Imports official PNGs from frontend/src/assets/brand/ only.
 *  - No base64 embedding, no external URLs, no distortion.
 *  - Official logos are displayed exactly as supplied: no CSS filter
 *    inversion and no brand-colour alteration. Aspect ratio preserved
 *    via object-fit: contain. On the dark header, logos sit inside
 *    white logo cards to guarantee contrast without altering the marks.
 *
 * Print rules:
 *  - class="report-cover-page" triggers break-after: page in PrintStyles.
 */
import { Box, Stack, Typography } from "@mui/material";
import type { ReportCoverSection } from "@flaha/shared-types";

import flahasoilLogo from "../../../../assets/brand/flahasoil-logo.png";
import flahaPaLogo from "../../../../assets/brand/flaha-pa-logo.png";
import flahaAgriTechLogo from "../../../../assets/brand/flaha-agri-tech-logo.png";

import { flahaSoilColors } from "../../../../theme/flahaSoilTheme";
import { ReportCoverMetadata } from "./ReportCoverMetadata";
import { ReportBrandStrip } from "./ReportBrandStrip";

export interface ReportCoverPageProps {
  cover: ReportCoverSection;
  versionNumber?: number | undefined;
}

/**
 * White logo card. Guarantees contrast for official marks on the dark
 * header without applying any filter or recolouring. The mark is rendered
 * exactly as supplied, with aspect ratio preserved (object-fit: contain).
 */
function LogoCard({ src, alt, height }: { src: string; alt: string; height: number }) {
  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: 1,
        px: 1.5,
        py: 1,
        display: "inline-flex",
        alignItems: "center",
        boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ height, width: "auto", objectFit: "contain", display: "block" }}
      />
    </Box>
  );
}

export function ReportCoverPage({ cover, versionNumber }: ReportCoverPageProps) {
  return (
    <Box
      className="report-cover-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "297mm",
        width: "100%",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        "@media print": {
          width: "210mm",
          minHeight: "297mm",
        },
      }}
    >
      {/* ── Header band ─────────────────────────────────── */}
      <Box
        sx={{
          backgroundColor: flahaSoilColors.deepSoilBrown,
          px: 5,
          pt: 5,
          pb: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Logo row — official marks shown as-supplied inside white logo
            cards for contrast on the dark header. No filters, no recolouring. */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <LogoCard src={flahasoilLogo} alt="FlahaSOIL" height={52} />
          <Stack direction="row" spacing={2} alignItems="center">
            <LogoCard src={flahaPaLogo} alt="Flaha PA" height={40} />
            <LogoCard src={flahaAgriTechLogo} alt="Flaha Agri Tech" height={40} />
          </Stack>
        </Stack>

        {/* Title block */}
        <Box>
          <Typography
            variant="overline"
            sx={{ color: flahaSoilColors.sandBeige, letterSpacing: "0.25em", fontSize: "0.75rem" }}
          >
            SOIL ANALYSIS REPORT
          </Typography>
          <Typography
            variant="h3"
            sx={{ color: "#FFFFFF", fontWeight: 700, lineHeight: 1.15, mt: 0.5, fontSize: { xs: "1.8rem", md: "2.4rem" } }}
          >
            {cover.reportTitle || "Professional Soil Report"}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: flahaSoilColors.sandBeige, mt: 1, fontStyle: "italic", opacity: 0.9 }}
          >
            From Soil Test to Field Action
          </Typography>
        </Box>
      </Box>

      {/* ── Accent line ─────────────────────────────────── */}
      <Box sx={{ height: 6, backgroundColor: flahaSoilColors.organicGreen }} />

      {/* ── Metadata section ─────────────────────────────── */}
      <ReportCoverMetadata cover={cover} versionNumber={versionNumber} />

      {/* ── Spacer to push strip to bottom ──────────────── */}
      <Box sx={{ flex: 1 }} />

      {/* ── Bottom value strip ───────────────────────────── */}
      <ReportBrandStrip />
    </Box>
  );
}
